import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import ContentStudio from "@/components/app/ContentStudio";
import SocialCalendarPanel from "@/components/app/SocialCalendarPanel";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LineChart, Line, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, RefreshCcw, Plus, Search, Sparkles, AlertTriangle, CheckCircle2,
  TrendingUp, TrendingDown, Minus, ExternalLink, Trash2, Zap, Download, History,
} from "lucide-react";
import OtterIcon from "@/components/app/OtterIcon";

function ScoreBadge({ score }) {
  let cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (score < 70) cls = "bg-amber-50 text-amber-800 border-amber-200";
  if (score < 50) cls = "bg-red-50 text-red-700 border-red-200";
  return <span className={`border px-2 py-0.5 font-mono-data text-xs ${cls}`}>{score}/100</span>;
}

async function downloadBlob(endpoint, fallbackFilename) {
  try {
    const res = await api.get(endpoint, { responseType: "blob" });
    const disposition = res.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match ? match[1] : fallbackFilename;
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
  }
}

function DiffArrow({ change }) {
  if (change > 0) return <span className="inline-flex items-center text-emerald-700"><TrendingUp className="w-3 h-3 mr-1" />+{change}</span>;
  if (change < 0) return <span className="inline-flex items-center text-red-700"><TrendingDown className="w-3 h-3 mr-1" />{change}</span>;
  return <span className="inline-flex items-center text-muted-foreground"><Minus className="w-3 h-3 mr-1" />0</span>;
}

function SimpleMd({ text }) {
  // Lightweight markdown renderer: headings, bullets, bold
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div className="space-y-3">
      {lines.map((line, i) => {
        if (line.startsWith("## ")) return <h3 key={i} className="font-semibold text-lg tracking-tight mt-4">{line.slice(3)}</h3>;
        if (line.startsWith("# ")) return <h2 key={i} className="font-semibold text-2xl tracking-tight mt-4">{line.slice(2)}</h2>;
        if (line.startsWith("- ") || line.startsWith("* ")) {
          const content = line.slice(2);
          return (
            <div key={i} className="flex gap-2 text-sm">
              <span className="text-primary">›</span>
              <span dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
            </div>
          );
        }
        if (line.trim() === "") return <div key={i} className="h-1" />;
        return <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />;
      })}
    </div>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("overview");
  const [keywords, setKeywords] = useState([]);
  const [newKw, setNewKw] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [backlinks, setBacklinks] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [newComp, setNewComp] = useState("");
  const [traffic, setTraffic] = useState([]);
  const [auditHistory, setAuditHistory] = useState([]);
  const [aiRecs, setAiRecs] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [ideasTopic, setIdeasTopic] = useState("");
  const [ideas, setIdeas] = useState("");
  const [ideasBusy, setIdeasBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: kws }, { data: lastAudit }, { data: bls }, { data: cps }, { data: tr }, { data: hist }] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/keywords`),
        api.get(`/projects/${projectId}/audit/latest`),
        api.get(`/projects/${projectId}/backlinks`),
        api.get(`/projects/${projectId}/competitors`),
        api.get(`/projects/${projectId}/traffic`),
        api.get(`/projects/${projectId}/audit/history?days=30`),
      ]);
      setProject(p); setKeywords(kws); setAudit(lastAudit); setBacklinks(bls); setCompetitors(cps); setTraffic(tr); setAuditHistory(hist);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load project");
    }
  };
  useEffect(() => { load(); }, [projectId]);

  const addKeyword = async (e) => {
    e.preventDefault();
    if (!newKw.trim()) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/keywords`, { keyword: newKw });
      setKeywords((k) => [data, ...k]);
      setNewKw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const delKeyword = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/keywords/${id}`);
      setKeywords((k) => k.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/audit`, {});
      setAudit(data);
      // Refresh history so the new audit appears on the chart immediately
      try {
        const { data: hist } = await api.get(`/projects/${projectId}/audit/history?days=30`);
        setAuditHistory(hist);
      } catch (e) { /* non-fatal */ }
      toast.success(`Audit complete · score ${data.score}/100`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Audit failed"); }
    setAuditing(false);
  };

  const addCompetitor = async (e) => {
    e.preventDefault();
    if (!newComp.trim()) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/competitors`, { domain: newComp });
      setCompetitors((c) => [...c, data]);
      setNewComp("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const delCompetitor = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/competitors/${id}`);
      setCompetitors((c) => c.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const genRecs = async () => {
    setAiBusy(true); setAiRecs("");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/recommendations`);
      setAiRecs(data.content);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI failed"); }
    setAiBusy(false);
  };

  const genIdeas = async (e) => {
    e.preventDefault();
    if (!ideasTopic.trim()) return;
    setIdeasBusy(true); setIdeas("");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/content-ideas`, { topic: ideasTopic });
      setIdeas(data.content);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI failed"); }
    setIdeasBusy(false);
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="p-10 text-sm font-mono-data text-muted-foreground">Loading project…</main>
      </div>
    );
  }

  const m = project.metrics || {};

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="px-4 md:px-8 py-8 max-w-7xl mx-auto" data-testid="project-detail">
        {/* Project header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Link to="/app" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-projects">
              <ArrowLeft className="w-3.5 h-3.5" /> All projects
            </Link>
            <h1 className="font-semibold tracking-tight text-4xl md:text-5xl mt-2">{project.name}</h1>
            <a href={project.url} target="_blank" rel="noreferrer" className="font-mono-data text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1">
              {project.domain} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border text-xs">
            {[
              { l: "Domain Auth", v: m.domain_authority ?? "—" },
              { l: "Traffic (mo)", v: (m.organic_traffic ?? 0).toLocaleString() },
              { l: "Keywords", v: (m.organic_keywords ?? 0).toLocaleString() },
              { l: "Backlinks", v: (m.backlinks ?? 0).toLocaleString() },
            ].map((s) => (
              <div key={s.l} className="bg-white px-4 py-3">
                <div className="text-muted-foreground font-mono-data uppercase tracking-widest">{s.l}</div>
                <div className="font-semibold text-xl mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="rounded-none border border-border bg-white p-0 h-auto flex flex-wrap">
            {[
              ["overview","Overview"],["keywords","Keywords"],["audit","Audit"],
              ["backlinks","Backlinks"],["competitors","Competitors"],["content","Content Studio"],["calendar","Calendar"],["ai","AI Insights"],
            ].map(([v,l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5" data-testid={`tab-${v}`}>
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-border bg-white p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-display tracking-tight text-lg">Estimated traffic · 30d</h3>
                  <span className="font-mono-data text-xs text-muted-foreground">VISITS</span>
                </div>
                <div className="h-64 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={traffic}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(var(--border))", fontFamily: "JetBrains Mono", fontSize: 11 }} />
                      <Area type="monotone" dataKey="visits" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.12} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="border border-border bg-white p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-display tracking-tight text-lg">Latest audit</h3>
                  <Button size="sm" onClick={runAudit} disabled={auditing} className="rounded-none h-8 bg-foreground hover:bg-foreground/90" data-testid="overview-run-audit">
                    {auditing ? "…" : <><RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Run</>}
                  </Button>
                </div>
                {audit ? (
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Score</span>
                      <ScoreBadge score={audit.score} />
                    </div>
                    <div className="flex justify-between font-mono-data text-xs"><span className="text-muted-foreground">ISSUES</span><span className="text-red-700">{audit.issues?.length ?? 0}</span></div>
                    <div className="flex justify-between font-mono-data text-xs"><span className="text-muted-foreground">WARNINGS</span><span className="text-amber-700">{audit.warnings?.length ?? 0}</span></div>
                    <div className="flex justify-between font-mono-data text-xs"><span className="text-muted-foreground">PASSED</span><span className="text-emerald-700">{audit.passed?.length ?? 0}</span></div>
                    <button onClick={()=>setTab("audit")} className="text-xs underline underline-offset-4 mt-2">View full report →</button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-4">No audit yet. Run your first crawl.</p>
                )}
              </div>
            </div>

            <div className="border border-border bg-white p-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  <h3 className="font-display tracking-tight text-lg">Score history · 30d</h3>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-data text-xs text-muted-foreground">{auditHistory.length} audit{auditHistory.length === 1 ? "" : "s"}</span>
                  <Button size="sm" variant="outline" onClick={() => downloadBlob(`/projects/${projectId}/audit/export.csv`, "audits.csv")}
                    className="rounded-none h-8" data-testid="export-audit-csv">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                </div>
              </div>
              {auditHistory.length < 2 ? (
                <p className="text-sm text-muted-foreground mt-3" data-testid="score-history-empty">
                  Need at least 2 audits to chart a trend. We&apos;ll auto-audit your site daily — come back tomorrow,
                  or <button className="underline" onClick={runAudit}>run another now</button>.
                </p>
              ) : (
                <div className="h-56 mt-4" data-testid="score-history-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={auditHistory.map(h => ({ ...h, dateShort: (h.date || "").slice(5, 10) }))}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                      <XAxis dataKey="dateShort" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(var(--border))", fontFamily: "JetBrains Mono", fontSize: 11 }} />
                      <Line type="monotone" dataKey="score" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 3, fill: "hsl(var(--foreground))" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="border border-border bg-white p-6">
              <h3 className="font-display tracking-tight text-lg">Top tracked keywords</h3>
              {keywords.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-2">No keywords tracked yet. <button className="underline" onClick={()=>setTab("keywords")}>Add some →</button></p>
              ) : (
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Keyword</TableHead>
                      <TableHead className="text-right">Volume</TableHead>
                      <TableHead className="text-right">Diff</TableHead>
                      <TableHead className="text-right">Position</TableHead>
                      <TableHead className="text-right">Δ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keywords.slice(0, 6).map((k) => (
                      <TableRow key={k.id}>
                        <TableCell className="font-medium">{k.keyword}</TableCell>
                        <TableCell className="text-right font-mono-data">{k.volume.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono-data">{k.difficulty}</TableCell>
                        <TableCell className="text-right font-mono-data">{k.position}</TableCell>
                        <TableCell className="text-right font-mono-data"><DiffArrow change={k.change} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* KEYWORDS */}
          <TabsContent value="keywords" className="mt-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <form onSubmit={addKeyword} className="flex gap-2 flex-1 max-w-2xl" data-testid="add-keyword-form">
                <Input value={newKw} onChange={(e)=>setNewKw(e.target.value)} placeholder="Add a keyword (e.g., best running shoes)"
                  className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                  data-testid="add-keyword-input" />
                <Button type="submit" className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="add-keyword-submit">
                  <Plus className="w-4 h-4 mr-1" /> Track
                </Button>
              </form>
              <Button variant="outline" disabled={keywords.length === 0}
                onClick={() => downloadBlob(`/projects/${projectId}/keywords/export.csv`, "keywords.csv")}
                className="rounded-none h-11" data-testid="export-keywords-csv">
                <Download className="w-4 h-4 mr-1.5" /> Export CSV
              </Button>
            </div>

            <div className="border border-border bg-white overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Keyword</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                    <TableHead className="text-right">Difficulty</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead>Intent</TableHead>
                    <TableHead className="text-right">Position</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keywords.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No keywords tracked yet.</TableCell></TableRow>
                  ) : keywords.map((k) => (
                    <TableRow key={k.id} data-testid={`keyword-row-${k.id}`}>
                      <TableCell className="font-medium">{k.keyword}</TableCell>
                      <TableCell className="text-right font-mono-data">{k.volume.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono-data">{k.difficulty}</TableCell>
                      <TableCell className="text-right font-mono-data">${k.cpc}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-none font-mono-data text-[10px] uppercase">{k.intent}</Badge></TableCell>
                      <TableCell className="text-right font-mono-data">{k.position}</TableCell>
                      <TableCell className="text-right font-mono-data"><DiffArrow change={k.change} /></TableCell>
                      <TableCell className="text-right">
                        <button onClick={()=>delKeyword(k.id)} className="text-muted-foreground hover:text-destructive" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* AUDIT */}
          <TabsContent value="audit" className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// on-page</p>
                <h3 className="font-semibold text-2xl tracking-tight">SEO Audit</h3>
              </div>
              <Button onClick={runAudit} disabled={auditing} className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="run-audit-button">
                {auditing ? "Auditing…" : <><Zap className="w-4 h-4 mr-2" /> Run audit</>}
              </Button>
            </div>
            {!audit ? (
              <div className="border border-dashed border-foreground/30 p-12 text-center">
                <div className="w-12 h-12 mx-auto bg-foreground grid place-items-center text-background mb-3">
                  <OtterIcon size={28} />
                </div>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Arlo hasn&apos;t audited a URL on this project yet. Run one above to inspect meta tags, headings, schema and performance.
                </p>
              </div>
            ) : (
              <>
                <div className="grid lg:grid-cols-4 gap-px border border-border bg-border">
                  {[
                    { l: "Score", v: `${audit.score}/100` },
                    { l: "Status", v: audit.status_code },
                    { l: "Response", v: `${audit.load_ms} ms` },
                    { l: "Words", v: (audit.word_count || 0).toLocaleString() },
                  ].map((s) => (
                    <div key={s.l} className="bg-white p-5">
                      <div className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">{s.l}</div>
                      <div className="font-semibold text-3xl mt-1">{s.v}</div>
                    </div>
                  ))}
                </div>

                <div className="grid lg:grid-cols-2 gap-6 mt-6">
                  <div className="border border-border bg-white p-6">
                    <h4 className="font-display tracking-tight text-lg flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Issues & warnings</h4>
                    <ul className="mt-4 space-y-2 text-sm">
                      {(audit.issues || []).map((it, i) => (
                        <li key={`i${i}`} className="flex gap-2"><span className="text-red-600">●</span><span>{it}</span></li>
                      ))}
                      {(audit.warnings || []).map((it, i) => (
                        <li key={`w${i}`} className="flex gap-2"><span className="text-amber-600">●</span><span>{it}</span></li>
                      ))}
                      {audit.issues?.length === 0 && audit.warnings?.length === 0 && (
                        <li className="text-muted-foreground">Clean. No issues found.</li>
                      )}
                    </ul>
                  </div>
                  <div className="border border-border bg-white p-6">
                    <h4 className="font-display tracking-tight text-lg flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Passed checks</h4>
                    <ul className="mt-4 space-y-2 text-sm">
                      {(audit.passed || []).map((it, i) => (
                        <li key={i} className="flex gap-2"><span className="text-emerald-600">●</span><span>{it}</span></li>
                      ))}
                      {audit.passed?.length === 0 && <li className="text-muted-foreground">No passed checks yet.</li>}
                    </ul>
                  </div>
                </div>

                <div className="border border-border bg-white p-6 mt-6 font-mono-data text-xs">
                  <h4 className="font-display tracking-tight text-lg font-sans mb-4">Page details</h4>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                    <div className="flex justify-between"><span className="text-muted-foreground">TITLE</span><span className="truncate ml-3">{audit.title || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">TITLE_LEN</span><span>{audit.title_length}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">META_DESC_LEN</span><span>{audit.meta_description_length}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">H1_COUNT</span><span>{audit.h1?.length ?? 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">H2_COUNT</span><span>{audit.h2_count}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">H3_COUNT</span><span>{audit.h3_count}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">IMG_TOTAL</span><span>{audit.images_total}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">IMG_NO_ALT</span><span>{audit.images_missing_alt}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">INT_LINKS</span><span>{audit.internal_links}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">EXT_LINKS</span><span>{audit.external_links}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">OG_TAGS</span><span>{audit.og_tags_count}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">SCHEMA</span><span>{audit.has_schema ? "yes" : "no"}</span></div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* BACKLINKS */}
          <TabsContent value="backlinks" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" disabled={backlinks.length === 0}
                onClick={() => downloadBlob(`/projects/${projectId}/backlinks/export.csv`, "backlinks.csv")}
                className="rounded-none h-10" data-testid="export-backlinks-csv">
                <Download className="w-4 h-4 mr-1.5" /> Export CSV
              </Button>
            </div>
            <div className="border border-border bg-white overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Anchor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">DA</TableHead>
                    <TableHead className="text-right">PA</TableHead>
                    <TableHead>First seen</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backlinks.map((b) => (
                    <TableRow key={b.id} data-testid={`backlink-row-${b.id}`}>
                      <TableCell className="font-medium">{b.source}</TableCell>
                      <TableCell className="font-mono-data text-xs">{b.anchor}</TableCell>
                      <TableCell><Badge variant={b.type === "dofollow" ? "default" : "outline"} className="rounded-none font-mono-data text-[10px] uppercase">{b.type}</Badge></TableCell>
                      <TableCell className="text-right font-mono-data">{b.domain_authority}</TableCell>
                      <TableCell className="text-right font-mono-data">{b.page_authority}</TableCell>
                      <TableCell className="font-mono-data text-xs">{b.first_seen}</TableCell>
                      <TableCell><a href={b.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"><ExternalLink className="w-3.5 h-3.5" /></a></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* COMPETITORS */}
          <TabsContent value="competitors" className="mt-6 space-y-4">
            <form onSubmit={addCompetitor} className="flex gap-2" data-testid="add-competitor-form">
              <Input value={newComp} onChange={(e)=>setNewComp(e.target.value)} placeholder="competitor.com"
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="add-competitor-input" />
              <Button type="submit" className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="add-competitor-submit">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </form>
            <div className="border border-border bg-white overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead className="text-right">DA</TableHead>
                    <TableHead className="text-right">Traffic</TableHead>
                    <TableHead className="text-right">Keywords</TableHead>
                    <TableHead className="text-right">Backlinks</TableHead>
                    <TableHead className="text-right">Ref Domains</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {competitors.map((c) => (
                    <TableRow key={c.id} className={c.is_self ? "bg-secondary/40" : ""} data-testid={`competitor-row-${c.id}`}>
                      <TableCell className="font-medium">{c.domain} {c.is_self && <Badge className="rounded-none ml-2 font-mono-data text-[10px] uppercase">you</Badge>}</TableCell>
                      <TableCell className="text-right font-mono-data">{c.domain_authority}</TableCell>
                      <TableCell className="text-right font-mono-data">{c.organic_traffic.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono-data">{c.organic_keywords.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono-data">{c.backlinks.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono-data">{c.referring_domains.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        {!c.is_self && (
                          <button onClick={()=>delCompetitor(c.id)} className="text-muted-foreground hover:text-destructive" aria-label="Remove">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CONTENT STUDIO */}
          <TabsContent value="content" className="mt-6">
            <ContentStudio projectId={projectId} projectDomain={project.domain} />
          </TabsContent>

          {/* CALENDAR */}
          <TabsContent value="calendar" className="mt-6">
            <SocialCalendarPanel scope="project" projectId={projectId} projects={[project]} />
          </TabsContent>

          {/* AI INSIGHTS */}
          <TabsContent value="ai" className="mt-6 space-y-6">
            <div className="border border-border bg-white p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// strategist</p>
                  <h3 className="font-semibold text-2xl tracking-tight flex items-center gap-2"><Sparkles className="w-5 h-5" /> AI action plan</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">Claude Sonnet 4.5 turns your audit + tracked keywords into a prioritized roadmap.</p>
                </div>
                <Button onClick={genRecs} disabled={aiBusy} className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="generate-recommendations-button">
                  {aiBusy ? "Thinking…" : "Generate plan"}
                </Button>
              </div>
              <div className="mt-6">
                {aiRecs ? <SimpleMd text={aiRecs} /> : (
                  <p className="text-sm text-muted-foreground">No plan generated yet.</p>
                )}
              </div>
            </div>

            <div className="border border-border bg-white p-6">
              <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// brainstorm</p>
              <h3 className="font-semibold text-2xl tracking-tight flex items-center gap-2"><Search className="w-5 h-5" /> Content ideas</h3>
              <form onSubmit={genIdeas} className="flex gap-2 mt-4" data-testid="content-ideas-form">
                <Input value={ideasTopic} onChange={(e)=>setIdeasTopic(e.target.value)} placeholder="Topic, e.g., 'remote work tools'"
                  className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                  data-testid="content-ideas-input" />
                <Button type="submit" disabled={ideasBusy} className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="content-ideas-submit">
                  {ideasBusy ? "Generating…" : "Brainstorm"}
                </Button>
              </form>
              <div className="mt-6">
                {ideas ? <SimpleMd text={ideas} /> : (
                  <p className="text-sm text-muted-foreground">Enter a topic to generate post ideas.</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
